/**
 * Helpers for the reservation submit flow.
 *
 * These exist to fix two production bugs:
 *  - blank error modals (errorMsg could end up "" and was shown verbatim)
 *  - duplicate eSignatures agreements on retry (no idempotency key client-side)
 */

export const SUPPORT_PHONE = '(614) 699-2495'

export const SUBMIT_ERROR_FALLBACK =
    `Something went wrong while submitting your reservation. Please try again — if it keeps ` +
    `happening, call us at ${SUPPORT_PHONE} and we'll get you set up.`

function extractNested(error: any): string | null {
    const data = error?.originalError?.response?.data
    if (!data || typeof data !== 'object') return null
    const keys = Object.keys(data)
    if (keys.length === 0) return null
    const nested = data[keys[0]]
    return nested?.message ? `"${keys[0]}": ${nested.message}` : null
}

/**
 * Build a human-readable error message from a submit failure.
 * GUARANTEE: never returns an empty/whitespace string — falls back to
 * SUBMIT_ERROR_FALLBACK so the error modal can never render blank.
 */
export function buildSubmitErrorMessage(error: any, requestDescription = 'Submit'): string {
    let msg = ''
    const data = error?.response?.data

    if (data && typeof data === 'object') {
        if (data.error) msg = `${requestDescription}: ${data.error}`

        if (data.details?.data && typeof data.details.data === 'object') {
            const fieldErrors = Object.entries(data.details.data)
                .map(([field, fe]: [string, any]) => (fe && fe.message ? `${field}: ${fe.message}` : null))
                .filter(Boolean) as string[]
            if (fieldErrors.length > 0) {
                msg = (msg ? msg + '\n\nDetails:\n' : `${requestDescription}:\n`) + fieldErrors.join('\n')
            }
        }

        if (!msg && data.details?.message) msg = `${requestDescription}: ${data.details.message}`
    } else if (typeof data === 'string' && data.trim()) {
        msg = `${requestDescription}: ${data.trim()}`
    } else {
        const nested = extractNested(error)
        if (nested) msg = `${requestDescription} - ${nested}`
        else if (error?.message) msg = `${requestDescription}: ${error.message}`
    }

    return msg.trim() || SUBMIT_ERROR_FALLBACK
}

/**
 * Stable identity for a reservation submission. If two submits produce the same
 * key, they describe the same reservation and must reuse the same eSignatures
 * agreement instead of minting a new one.
 */
export function computeSubmissionKey(values: any, spaceId: string | null | undefined): string {
    return [
        spaceId || '',
        values?.email || '',
        values?.vin || '',
        values?.vehicle_make || '',
        values?.vehicle_model || '',
        values?.move_in_date || '',
    ].join('|').trim().toLowerCase()
}
